import { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { signInWithPopup, signOut, onAuthStateChanged } from 'firebase/auth'
import { auth, googleProvider } from '../lib/firebase'
import logger from '../lib/logger'
import { thirdLogin } from '../api/auth'
import { cacheImage, clearCachedImage } from '../lib/imageCache'

export interface User {
  uid: string
  name: string
  email: string
  avatar?: string
  credits: number
  plan: 'Free' | 'Pro'
  token?: string
}

interface AuthContextType {
  user: User | null
  login: () => Promise<void>
  logout: () => Promise<void>
  isLoading: boolean
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    logger.debug('Setting up Firebase auth state listener')

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      logger.debug('Firebase auth state changed:', {
        hasFirebaseUser: !!firebaseUser,
        email: firebaseUser?.email,
        uid: firebaseUser?.uid,
      })

      if (firebaseUser) {
        try {
          // Check if we have a backend token stored
          const backendToken = localStorage.getItem('auth_token')
          logger.debug('Backend token check:', {
            hasBackendToken: !!backendToken,
            tokenLength: backendToken?.length,
          })

          // Get Firebase ID token
          const idToken = await firebaseUser.getIdToken()

          if (backendToken) {
            // If we have a backend token, verify it by calling backend with fresh ID token
            logger.debug('Verifying existing backend token with fresh Firebase ID token')
            try {
              const response = await thirdLogin({
                provider: 'google',
                idToken: idToken,
              })

              logger.info('Backend authentication verified successfully')

              // Update token and user state
              localStorage.setItem('auth_token', response.token)
              setUser({
                uid: response.user.id,
                name: response.user.displayName,
                email: response.user.email,
                avatar: response.user.avatarUrl,
                credits: 10, // TODO: Get from backend when available
                plan: 'Free', // TODO: Get from backend when available
                token: response.token,
              })

              // Cache avatar in background
              if (response.user.avatarUrl) {
                cacheImage(response.user.id, response.user.avatarUrl).catch((error) => {
                  logger.warn('Failed to cache avatar on token verification:', error)
                })
              }
            } catch (error) {
              // Backend verification failed, clear token and sign out
              logger.error('Backend token verification failed:', error)
              localStorage.removeItem('auth_token')
              await signOut(auth)
              setUser(null)
            }
          } else {
            // No backend token - this happens during login flow
            // Try to authenticate with backend to get a token
            logger.debug('No backend token found - attempting to authenticate with backend')
            try {
              const response = await thirdLogin({
                provider: 'google',
                idToken: idToken,
              })

              logger.info('Backend authentication successful (initial login)')

              // Store token and update user state
              localStorage.setItem('auth_token', response.token)
              setUser({
                uid: response.user.id,
                name: response.user.displayName,
                email: response.user.email,
                avatar: response.user.avatarUrl,
                credits: 10, // TODO: Get from backend when available
                plan: 'Free', // TODO: Get from backend when available
                token: response.token,
              })

              // Cache avatar in background
              if (response.user.avatarUrl) {
                cacheImage(response.user.id, response.user.avatarUrl).catch((error) => {
                  logger.warn('Failed to cache avatar on initial login:', error)
                })
              }
            } catch (error) {
              // Backend authentication failed, sign out
              logger.error('Backend authentication failed:', error)
              await signOut(auth)
              setUser(null)
            }
          }
        } catch (error) {
          logger.error('Error during auth state change:', error)
          localStorage.removeItem('auth_token')
          await signOut(auth)
          setUser(null)
        }
      } else {
        // No Firebase user, clear everything
        logger.debug('No Firebase user - clearing auth state')
        localStorage.removeItem('auth_token')
        setUser(null)
      }
      setIsLoading(false)
    })

    return () => {
      logger.debug('Cleaning up Firebase auth state listener')
      unsubscribe()
    }
  }, [])

  const login = async () => {
    try {
      // Force account selection every time
      googleProvider.setCustomParameters({ prompt: 'select_account' })

      logger.debug('Initiating Firebase sign-in popup')
      await signInWithPopup(auth, googleProvider)

      // onAuthStateChanged will handle the rest (backend authentication and state update)
      logger.debug('Firebase sign-in successful, waiting for auth state change handler')
    } catch (error) {
      logger.error('Login failed:', error)
      throw error
    }
  }

  const logout = async () => {
    try {
      // Clear cached avatar before signing out
      if (user?.uid) {
        clearCachedImage(user.uid)
      }

      await signOut(auth)
      // Clear the backend token
      localStorage.removeItem('auth_token')
    } catch (error) {
      logger.error('Logout failed:', error)
      throw error
    }
  }

  return (
    <AuthContext.Provider value={{ user, login, logout, isLoading }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
